import React, { useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Breadcrumb } from "react-bootstrap";
import { useRecoilValue } from "recoil";

import { allRoutes } from "src/constants/AllRoutes";
import { truncateText } from "src/utils/truncateAndRemoveImages";
import { breadCrumbAtom } from "src/recoilState/breadCrumb";
import { Strings } from "src/resources";
import { parseServiceEditFromSupplierState } from "src/utils/serviceEditFromSupplier";
import "./custom-breadcrumb.scss";

const CustomBreadcrumb: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();

  const breadCrumbName = useRecoilValue(breadCrumbAtom);
  const MAX_LABEL_LENGTH = 30;

  // Process and filter path names
  const processedPathNames = useMemo(() => {
    const pathNames = location.pathname.split("/").filter(Boolean);

    const supplierEditCtx = parseServiceEditFromSupplierState(location.state);
    if (pathNames[0] === "services" && pathNames.length === 3 && pathNames[2] === "edit" && supplierEditCtx) {
      const { supplierId, supplierName } = supplierEditCtx;
      return [
        { label: Strings.supplier, path: allRoutes.suppliers },
        { label: supplierName || Strings.view_supplier, path: `${allRoutes.suppliers}/${supplierId}` },
        { label: Strings.edit, path: location.pathname }
      ];
    }

    // Special handling for specific route patterns
    if ((pathNames[0] === "agency" || pathNames[0] === "agent") && pathNames.some((name) => name === "edit")) {
      pathNames.splice(1, 1);
      pathNames[1] = "edit-" + pathNames[0];
    }

    // Check if path includes "services/add" and replace "add" with "Add New Service"
    if (pathNames[0] === "services" && pathNames[1] === "add") {
      pathNames[1] = "Add New Service";
    }

    if (pathNames[0] === "hotels" && pathNames[1] === "add") {
      pathNames[1] = "Add Hotel";
    }


    if (pathNames[0] === "event" && pathNames[1] === "add") {
      pathNames[1] = "Add New Event";
    }

    return pathNames.map((name, index) => ({
      label:
        name === id
          ? pathNames[0] === "hotels" && pathNames.length > 1
            ? "View Hotel"
            : pathNames[0] === "services" && pathNames.length > 1
            ? "View Services"
            : pathNames[0] === "event" && pathNames.length > 1
            ? "View Event Details"
            : breadCrumbName?.locationName || name
          : name,
      path: `/${pathNames.slice(0, index + 1).join("/")}`
    }));
  }, [location.pathname, location.state, id, breadCrumbName]);

  // Handle breadcrumb item navigation
  const handleNavigate = (path: string) => {
    // navigate(path);
    if (path.startsWith(`${allRoutes.suppliers}/`)) {
      const parts = path.split("/").filter(Boolean);
      const segmentAfterSupplier = parts[1];
      if (parts.length === 2 && segmentAfterSupplier && /^[A-Za-z0-9]{20}$/.test(segmentAfterSupplier)) {
        navigate(path);
        return;
      }
      navigate(allRoutes.suppliers);
      return;
    } else if (path.startsWith("/region/")) {
      // Extract '/region' and navigate
      navigate("/region");
    } else {
      // If path doesn't match expected formats, navigate as is
      navigate(path);
    }
  };

  // No paths exist - show dashboard
  if (processedPathNames.length === 0) {
    return (
      <Breadcrumb className="custom-breadcrumb" aria-label="Breadcrumb navigation">
        <Breadcrumb.Item active>{Strings.dashboard}</Breadcrumb.Item>
      </Breadcrumb>
    );
  }

  return (
    <Breadcrumb className="custom-breadcrumb" aria-label="Breadcrumb navigation">
      {processedPathNames.map((item, index) => {
        const isLastItem = index === processedPathNames.length - 1;
        const displayLabel = truncateText(item.label.replaceAll("-", " "), MAX_LABEL_LENGTH);

        return isLastItem ? (
          <Breadcrumb.Item key={item.path} active data-cy={item.label}>
            <span title={item.label.replaceAll("-", " ")}>{displayLabel}</span>
          </Breadcrumb.Item>
        ) : (
          <Breadcrumb.Item key={item.path} onClick={() => handleNavigate(item.path)}>
            <span className="breadcrumb-text" title={item.label.replaceAll("-", " ")}>
              {displayLabel}
            </span>
          </Breadcrumb.Item>
        );
      })}
    </Breadcrumb>
  );
};

export default CustomBreadcrumb;
